import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PasswordInput from '../components/PasswordInput';
import CredentialSuccessModal from '../components/CredentialSuccessModal';
import api from '../api';

const emptyForm = { name: '', role: 'Security Guard', contactNo: '', salary: '', status: 'Active', createAccount: true, accountUsername: '', accountPassword: '' };
const phoneInput = value => String(value || '').replace(/\D/g, '').slice(0, 11);

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/staff').then(r => setStaff(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(emptyForm); setEditId(null); setEditAccount(null); setShowModal(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, role: s.role, contactNo: s.contactNo || '', salary: s.salary || '', status: s.status, createAccount: !s.account, accountUsername: '', accountPassword: '' });
    setEditId(s._id);
    setEditAccount(s.account || null);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const { createAccount, accountUsername, accountPassword, ...profileData } = form;
        await api.put(`/staff/${editId}`, profileData);
        if (!editAccount && createAccount) {
          const accountResponse = await api.post('/user-accounts', { role: 'staff', personId: editId, username: accountUsername, password: accountPassword });
          setCredentials({ name: form.name, username: accountResponse.data.account.username, password: accountResponse.data.temporaryPassword });
        }
      } else {
        const response = await api.post('/staff', form);
        if (response.data.account) setCredentials({ name: response.data.name, username: response.data.account.username, password: response.data.temporaryPassword });
      }
      setShowModal(false);
      setMsg(editId ? 'Staff updated!' : 'Staff added!');
      load();
    } catch (err) { setMsg(err.response?.data?.message || 'Error'); }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    await api.delete(`/staff/${id}`);
    load();
  };

  return (
    <Layout title="Staff Management">
      {credentials && <CredentialSuccessModal
        credential={credentials}
        title={editId ? 'Staff login created!' : 'Staff member added successfully!'}
        onClose={() => setCredentials(null)}
      />}
      {msg && <div className="alert alert-info">{msg} <button style={{ float:'right', background:'none', border:'none', cursor:'pointer' }} onClick={() => setMsg('')}>✕</button></div>}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">👥 Staff Members</h3>
          <button className="btn btn-primary" onClick={openAdd}>➕ Add Staff</button>
        </div>

        {loading ? (
          <div className="text-center" style={{ padding: 40 }}>Loading...</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Name</th><th>Role</th><th>Contact</th><th>Monthly Salary</th><th>Portal Account</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {staff.length === 0 ? (
                  <tr><td colSpan={7} className="text-center" style={{ padding: 30, color: '#718096' }}>No staff added yet</td></tr>
                ) : staff.map(s => (
                  <tr key={s._id}>
                    <td><strong>{s.name}</strong></td>
                    <td>
                      <span className={`badge ${s.role === 'Security Guard' ? 'badge-warning' : 'badge-info'}`}>
                        {s.role === 'Security Guard' ? '🛡️' : '🧹'} {s.role}
                      </span>
                    </td>
                    <td>{s.contactNo || '-'}</td>
                    <td>PKR {(s.salary || 0).toLocaleString()}</td>
                    <td>{s.account ? <div><strong style={{ fontSize: 12 }}>@{s.account.username}</strong><span className={`badge ${s.account.status === 'Active' ? 'badge-success' : 'badge-secondary'}`} style={{ marginLeft: 6 }}>{s.account.status}</span></div> : <span className="badge badge-warning">No login</span>}</td>
                    <td><span className={`badge ${s.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>{s.status}</span></td>
                    <td>
                      <div className="flex gap-10">
                        <button className="btn btn-warning btn-sm" onClick={() => openEdit(s)}>✏️ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteStaff(s._id)}>🗑 Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editId ? 'Edit Staff' : 'Add Staff Member'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSave} autoComplete="off">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name *</label>
                    <input value={form.name} maxLength={100} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                      <option>Security Guard</option>
                      <option>Cleaning Staff</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Contact No</label>
                    <input value={form.contactNo} onChange={e => setForm(f => ({ ...f, contactNo: phoneInput(e.target.value) }))} inputMode="numeric" pattern="03[0-9]{9}" maxLength={11} placeholder="03XXXXXXXXX" title="Enter 11 digits starting with 03" />
                    <small>11 digits, starting with 03</small>
                  </div>
                  <div className="form-group">
                    <label>Monthly Salary (PKR)</label>
                    <input type="number" value={form.salary} onChange={e => setForm(f => ({ ...f, salary: e.target.value }))} min="0" placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1', padding: 14, border: '1px solid #dbe9e4', borderRadius: 8, background: '#f5faf8' }}>
                    <label>Portal Login</label>
                    {editAccount ? <p style={{ fontSize: 12, marginTop: 6 }}><strong>@{editAccount.username}</strong> <span className={`badge ${editAccount.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>{editAccount.status}</span></p> : <><label style={{ flexDirection: 'row', alignItems: 'center', gap: 8, margin: '7px 0', textTransform: 'none' }}><input type="checkbox" checked={form.createAccount} onChange={e => setForm(f => ({ ...f, createAccount: e.target.checked }))} /> Create staff login now</label>{form.createAccount && <div className="form-grid"><div className="form-group"><label>Username (optional)</label><input name="new-staff-portal-username" autoComplete="off" value={form.accountUsername} maxLength={40} pattern="[A-Za-z0-9][A-Za-z0-9._-]{3,39}" title="Use 4-40 letters, numbers, dots, dashes or underscores" onChange={e => setForm(f => ({ ...f, accountUsername: e.target.value }))} /><small>Leave blank to create a username automatically.</small></div><div className="form-group"><label>Temporary password (optional)</label><PasswordInput name="new-staff-portal-password" autoComplete="new-password" value={form.accountPassword} minLength={12} maxLength={100} onChange={e => setForm(f => ({ ...f, accountPassword: e.target.value }))} /><small>Leave blank for an easy-to-read 12-character password.</small></div></div>}</>}
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
